<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import * as XLSX from 'xlsx'
import { $api } from '../../utils/api'
import { CSVParser } from '../../utils/csv-parser'
import { formatDate24h } from '@/utils/dateFormatter'
import { useToast } from '@/composables/useToast'

const { t } = useI18n()
const { showToast } = useToast()

// Define page meta for CASL permissions
definePage({
  meta: {
    action: 'create',
    subject: 'CallList',
  },
})

const numberedSteps = computed(() => [
  {
    title: t('calllist.uploadFile'),
    subtitle: t('calllist.wizardDescription'),
  },
  {
    title: t('calllist.mapFields'),
    subtitle: t('calllist.mapFieldsDescription'),
  },
  {
    title: t('calllist.callListDetails'),
    subtitle: t('calllist.enterCallListInformation'),
  },
])

const currentStep = ref(0)
const loading = ref(false)

// File upload
const uploadedFile = ref<File | null>(null)
const filePreview = ref<any[]>([])
const csvHeaders = ref<string[]>([])
const isDragOver = ref(false)
const detectedEncoding = ref<string>('utf-8')
const encodingError = ref<string>('')

// Available fields from Lead model + CustomField
const availableFields = ref<Array<{
  key: string
  label: string
  type: string
  required: boolean
  source: 'lead' | 'custom'
  validation?: {
    required?: boolean
    minLength?: number
    maxLength?: number
    min?: number
    max?: number
    pattern?: string
    enum?: string[]
    options?: any[]
  }
}>>([])

// Validation rules from backend
const validationRules = ref<{
  requiredFields: string[]
  fieldValidations?: Record<string, {
    required?: boolean
    minLength?: number
    maxLength?: number
    min?: number
    max?: number
    pattern?: string
    enum?: string[]
    message?: string
  }>
  businessRules?: Record<string, {
    fields: string[]
    rule: 'at_least_one_required' | 'all_required' | 'exactly_one_required'
    message: string
  }>
}>({
  requiredFields: [],
})

// Field mapping
const fieldMapping = ref<Record<string, string>>({})

// CallList selection for field mapping reuse
const selectedCallListId = ref<number | null>(null)

const availableCallLists = ref<Array<{
  id: number
  importKey: string
  listName: string
  fieldMapping: Record<string, string> | null
  createdAt: string
}>>([])

const loadingCallLists = ref(false)

// Initialize field mapping
const initializeFieldMapping = () => {
  availableFields.value.forEach(field => {
    fieldMapping.value[field.key] = ''
  })
}

// Load available call lists with field mappings
const loadAvailableCallLists = async () => {
  try {
    loadingCallLists.value = true

    const response = await $api('/call-lists/field-mapping/reuse?limit=20')

    availableCallLists.value = response || []
  }
  catch (error) {
    availableCallLists.value = []
  }
  finally {
    loadingCallLists.value = false
  }
}

// Load available DNC groups
const loadAvailableDncGroups = async () => {
  try {
    loadingDncGroups.value = true

    const response = await $api('/dnc/groups/active')

    availableDncGroups.value = response || []
  }
  catch (error) {
    availableDncGroups.value = []
  }
  finally {
    loadingDncGroups.value = false
  }
}

// Fetch available fields from backend
const fetchAvailableFields = async () => {
  try {
    const response = await $api('/call-lists/field-mapping')

    availableFields.value = response.fields
    validationRules.value = response.validationRules
  }

  catch (error) {
    // Fallback to basic fields if API fails
    availableFields.value = [
      {
        key: 'firstName',
        label: t('calllist.fieldLabels.firstName'),
        type: 'text',
        required: true,
        source: 'lead',
        validation: {
          required: true,
          minLength: 1,
          maxLength: 100,
        },
      },
      {
        key: 'lastName',
        label: t('calllist.fieldLabels.lastName'),
        type: 'text',
        required: true,
        source: 'lead',
        validation: {
          required: true,
          minLength: 1,
          maxLength: 100,
        },
      },
      {
        key: 'telNo',
        label: t('calllist.fieldLabels.phoneNumber'),
        type: 'phone',
        required: false,
        source: 'lead',
        validation: {
          required: false,
          pattern: '^[0-9\\-\\+\\(\\)\\s]*$',
          maxLength: 20,
        },
      },
      {
        key: 'mobileNo',
        label: t('calllist.fieldLabels.mobileNumber'),
        type: 'phone',
        required: false,
        source: 'lead',
        validation: {
          required: false,
          pattern: '^[0-9\\-\\+\\(\\)\\s]*$',
          maxLength: 20,
        },
      },
    ]

    validationRules.value = {
      requiredFields: ['firstName', 'lastName'],
      fieldValidations: {
        firstName: {
          required: true,
          minLength: 1,
          maxLength: 100,
          pattern: '^[a-zA-Zก-๙0-9\\s]+$',
          message: t('calllist.errorMessages.firstNameValidation'),
        },
        lastName: {
          required: true,
          minLength: 1,
          maxLength: 100,
          pattern: '^[a-zA-Zก-๙0-9\\s]+$',
          message: t('calllist.errorMessages.lastNameValidation'),
        },
        telNo: {
          required: false,
          maxLength: 20,
          pattern: '^[0-9\\-\\+\\(\\)\\s]*$',
          message: t('calllist.errorMessages.phoneValidation'),
        },
        mobileNo: {
          required: false,
          maxLength: 20,
          pattern: '^[0-9\\-\\+\\(\\)\\s]*$',
          message: t('calllist.errorMessages.mobileValidation'),
        },
      },
      businessRules: {
        phoneNumberRequired: {
          fields: ['telNo', 'mobileNo'],
          rule: 'at_least_one_required',
          message: t('calllist.errorMessages.phoneRequired'),
        },
      },
    }
  }
}

// CallList details
const callListDetails = ref({
  importKey: '',
  callListId: null as number | null,
  listName: '',
  listDetail: '',
  listComment: '',
  fileName: '',
  fileType: '',
  fileSize: 0,
  totalRecords: 0,
  startDate: '',
  endDate: '',
})

// DNC Groups
const selectedDncGroups = ref<number[]>([])
const availableDncGroups = ref<Array<{
  id: number
  name: string
  description: string
  isActive: boolean
}>>([])
const loadingDncGroups = ref(false)

const importSummary = ref({
  totalRecords: 0,
  validRecords: 0,
  invalidRecords: 0,
  fileDuplicates: 0,
  calllistDuplicates: 0,
  dncRecords: 0, // เพิ่ม DNC count
  badListRecords: 0,
  importedRecords: 0,
})

const isImportCompleted = ref(false)

// Import type selection
const importType = ref('')

// Progress tracking for import process
const importProgress = ref({
  isProcessing: false,
  progress: 0,
  currentStep: '',
  error: null as string | null,
})

const router = useRouter()
const fileInput = ref<HTMLInputElement>()

// File upload handlers
const parseFile = async (file: File) => {
  try {
    // Reset encoding info
    encodingError.value = ''
    detectedEncoding.value = 'utf-8'

    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'))
    let headers: string[] = []
    let data: any[] = []
    let jsonData: any[] = [] // Define jsonData for Excel files

    // Handle Excel files (.xlsx, .xls)
    if (fileExtension === '.xlsx' || fileExtension === '.xls') {
      detectedEncoding.value = 'excel'

      const buffer = await file.arrayBuffer()
      const workbook = XLSX.read(buffer, { type: 'array' })

      // Get the first worksheet
      const firstSheetName = workbook.SheetNames[0]
      const worksheet = workbook.Sheets[firstSheetName]

      // Convert to JSON
      jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 })

      if (jsonData.length > 0) {
        // First row is headers
        headers = (jsonData[0] as string[]).map(h => String(h || '').trim())

        // Rest is data
        data = jsonData.slice(1, 6).map((row: unknown) => {
          const rowArray = row as any[]

          const rowData: any = {}

          headers.forEach((header, index) => {
            rowData[header] = String(rowArray[index] || '').trim()
          })

          return rowData
        })
      }
    }

    // Handle CSV files with enhanced parsing
    else {
      try {
        // Use enhanced CSV parser with automatic encoding detection
        const parseResult = await CSVParser.parseFile(file, {
          delimiter: ',',
          skipEmptyLines: true,
          header: true,
          preview: 0, // Parse all rows for preview
        })

        // Update detected encoding
        detectedEncoding.value = parseResult.encoding

        // Set headers and data
        headers = parseResult.headers
        data = parseResult.data

        // Validate CSV structure
        const validation = CSVParser.validateCSV(parseResult)
        if (!validation.isValid)

        // Show encoding errors if any
        {
          if (parseResult.errors.length > 0)
            encodingError.value = parseResult.errors.join('; ')
        }
      }
      catch (csvError) {
        // Fallback to basic CSV parsing
        let text = ''
        let usedEncoding = 'utf-8'

        try {
          text = await file.text()
        }
        catch (error) {
          const buffer = await file.arrayBuffer()
          const decoder = new TextDecoder('windows-1252')

          text = decoder.decode(buffer)
          usedEncoding = 'windows-1252'
        }

        // If text contains garbled characters, try other encodings
        if (text.includes('') || text.includes('ï¿½')) {
          const buffer = await file.arrayBuffer()
          const encodings = ['utf-8', 'windows-1252', 'iso-8859-1', 'gb2312', 'big5']

          for (const encoding of encodings) {
            try {
              const decoder = new TextDecoder(encoding)
              const testText = decoder.decode(buffer)

              if (!testText.includes('') && !testText.includes('ï¿½')) {
                text = testText
                usedEncoding = encoding
                break
              }
            }
            catch (e) {
              continue
            }
          }
        }

        detectedEncoding.value = usedEncoding

        const lines = text.split('\n').filter(line => line.trim())

        if (lines.length > 0) {
          const parseCSVLine = (line: string): string[] => {
            const result: string[] = []
            let current = ''
            let inQuotes = false

            for (let i = 0; i < line.length; i++) {
              const char = line[i]

              if (char === '"') {
                inQuotes = !inQuotes
              }
              else if (char === ',' && !inQuotes) {
                result.push(current.trim())
                current = ''
              }
              else {
                current += char
              }
            }

            result.push(current.trim())

            return result.map(field => {
              if (field.startsWith('"') && field.endsWith('"'))
                return field.slice(1, -1)

              return field
            })
          }

          headers = parseCSVLine(lines[0])
          data = lines.slice(1, 6).map(line => {
            const values = parseCSVLine(line)
            const row: any = {}

            headers.forEach((header, index) => {
              row[header] = values[index] || ''
            })

            return row
          })
        }
      }
    }

    // Set the parsed data
    if (headers.length > 0) {
      csvHeaders.value = headers
      filePreview.value = data

      // Update total records count
      if (fileExtension === '.xlsx' || fileExtension === '.xls') {
        // For Excel files, use jsonData.length - 1 (total rows minus header)
        callListDetails.value.totalRecords = jsonData.length - 1
      }
      else {
        // For CSV files, use data.length (already parsed by CSVParser)
        callListDetails.value.totalRecords = data.length
      }
    }
  }
  catch (error) {
    // Show error message to user
    filePreview.value = []
    csvHeaders.value = []
    encodingError.value = 'Unable to parse file. Please check file format and try again.'
  }
}

// Generate unique 13-digit import key
const generateImportKey = () => {
  const timestamp = Date.now().toString()
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0')

  return timestamp.slice(-10) + random
}

const handleFileUpload = (file: File) => {
  // Reset previous data
  uploadedFile.value = file
  isImportCompleted.value = false

  // Set file details
  callListDetails.value.fileName = file.name
  callListDetails.value.fileType = file.name.split('.').pop()?.toUpperCase() || ''
  callListDetails.value.fileSize = file.size
  callListDetails.value.importKey = generateImportKey()

  encodingError.value = ''
  detectedEncoding.value = 'utf-8'

  // Parse file (CSV or Excel)
  parseFile(file)

  // Initialize field mapping
  initializeFieldMapping()
}

const onDrop = (event: DragEvent) => {
  event.preventDefault()
  isDragOver.value = false

  const files = event.dataTransfer?.files
  if (files && files.length > 0)
    handleFileUpload(files[0])
}

const onDragOver = (event: DragEvent) => {
  event.preventDefault()
  isDragOver.value = true
}

const onDragLeave = () => {
  isDragOver.value = false
}

const onFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement
  const files = target.files
  if (files && files.length > 0) {
    handleFileUpload(files[0])
    target.value = ''
  }
}

// Levenshtein distance algorithm
const levenshteinDistance = (str1: string, str2: string): number => {
  const matrix = []

  for (let i = 0; i <= str2.length; i++)
    matrix[i] = [i]

  for (let j = 0; j <= str1.length; j++)
    matrix[0][j] = j

  for (let i = 1; i <= str2.length; i++) {
    for (let j = 1; j <= str1.length; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1]
      }
      else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1,
        )
      }
    }
  }

  return matrix[str2.length][str1.length]
}

// Calculate string similarity (simple implementation)
const calculateSimilarity = (str1: string, str2: string): number => {
  const longer = str1.length > str2.length ? str1 : str2
  const shorter = str1.length > str2.length ? str2 : str1

  if (longer.length === 0)
    return 1.0

  const distance = levenshteinDistance(longer, shorter)

  return (longer.length - distance) / longer.length
}

// Auto mapping logic
const autoMapFields = async () => {
  if (csvHeaders.value.length === 0)
    return

  // Clear existing mapping
  availableFields.value.forEach(field => {
    fieldMapping.value[field.key] = ''
  })

  // Check if a call list is selected for field mapping reuse
  if (selectedCallListId.value) {
    try {
      const response = await $api(`/call-lists/field-mapping/reuse/${selectedCallListId.value}`)
      if (response?.fieldMapping) {
        // Use field mapping from selected call list
        Object.entries(response.fieldMapping).forEach(([fieldKey, csvColumn]) => {
          // Check if the CSV column exists in current file
          if (csvHeaders.value.includes(csvColumn as string))
            fieldMapping.value[fieldKey] = csvColumn as string
        })

        return // Exit early if using existing field mapping
      }
    }
    catch (error) {

      // Fall through to normal auto mapping
    }
  }

  // Define mapping patterns for common field names
  const mappingPatterns: Record<string, string[]> = {
    firstName: [
      'first_name',
      'firstname',
      'first name',
      'fname',
      'given_name',
      'givenname',
      'given name',
      'ชื่อ',
      'first',
      'f_name',
      'firstname',
      'ชื่อจริง',
    ],
    lastName: [
      'last_name',
      'lastname',
      'last name',
      'lname',
      'surname',
      'family_name',
      'familyname',
      'family name',
      'นามสกุล',
      'last',
      'l_name',
      'lastname',
      'นามสกุล',
    ],
    telNo: [
      'tel_no',
      'telno',
      'tel no',
      'phone',
      'telephone',
      'tel',
      'phone_number',
      'phonenumber',
      'phone number',
      'เบอร์โทร',
      'เบอร์โทรศัพท์',
      'โทรศัพท์',
      'phone_no',
      'tel_no',
    ],
    mobileNo: [
      'mobile_no',
      'mobileno',
      'mobile no',
      'mobile',
      'mobile_number',
      'mobilenumber',
      'mobile number',
      'เบอร์มือถือ',
      'มือถือ',
      'mobile_no',
      'cell',
      'cellphone',
      'cell_phone',
      'cellphone',
    ],
    gender: [
      'gender',
      'sex',
      'เพศ',
      'เพศ',
      'male_female',
      'male/female',
    ],
    province: [
      'province',
      'state',
      'region',
      'จังหวัด',
      'area',
      'location',
      'city',
      'town',
    ],
    age: [
      'age',
      'อายุ',
      'years_old',
      'years old',
      'year',
      'years',
    ],

    // Add custom field patterns
    custom_company: [
      'company',
      'company_name',
      'companyname',
      'company name',
      'บริษัท',
      'organization',
      'org',
    ],
    custom_department: [
      'department',
      'dept',
      'section',
      'division',
      'แผนก',
      'หน่วยงาน',
    ],
    custom_email: [
      'email',
      'email_address',
      'emailaddress',
      'email address',
      'e_mail',
      'mail',
      'อีเมล',
      'อีเมล์',
    ],
  }

  // Auto mapping based on similarity
  csvHeaders.value.forEach(header => {
    const normalizedHeader = header.toLowerCase().trim().replace(/[_\s-]/g, '_')

    // Find best match for each field
    Object.entries(mappingPatterns).forEach(([fieldKey, patterns]) => {
      if (fieldMapping.value[fieldKey])
        return // Already mapped

      // Check for exact matches
      if (patterns.some(pattern => normalizedHeader === pattern.toLowerCase())) {
        fieldMapping.value[fieldKey] = header

        return
      }

      // Check for partial matches
      if (patterns.some(pattern => {
        const normalizedPattern = pattern.toLowerCase().replace(/[_\s-]/g, '_')

        return normalizedHeader.includes(normalizedPattern) || normalizedPattern.includes(normalizedHeader)
      }))
        fieldMapping.value[fieldKey] = header
    })
  })

  // Additional fuzzy matching for remaining unmapped fields
  csvHeaders.value.forEach(header => {
    const normalizedHeader = header.toLowerCase().trim()

    // Find unmapped fields
    const unmappedFields = availableFields.value.filter(field => !fieldMapping.value[field.key])

    unmappedFields.forEach(field => {
      if (fieldMapping.value[field.key])
        return

      // Simple similarity check
      const similarity = calculateSimilarity(normalizedHeader, field.label.toLowerCase())
      if (similarity > 0.6) { // 60% similarity threshold
        fieldMapping.value[field.key] = header
      }
    })
  })
}

// Data validation functions - for actual data import
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _validateImportData = (data: any[]) => {
  const errors: string[] = []
  const warnings: string[] = []

  data.forEach((row, index) => {
    const rowNumber = index + 1

    // Check required fields
    validationRules.value.requiredFields.forEach(fieldKey => {
      const mappedColumn = fieldMapping.value[fieldKey]
      if (mappedColumn && (!row[mappedColumn] || row[mappedColumn].toString().trim() === ''))
        errors.push(`Row ${rowNumber}: ${availableFields.value.find(f => f.key === fieldKey)?.label || fieldKey} is required`)
    })

    // Check field-specific validations
    if (validationRules.value.fieldValidations) {
      Object.entries(validationRules.value.fieldValidations).forEach(([fieldKey, rules]) => {
        const mappedColumn = fieldMapping.value[fieldKey]
        if (mappedColumn && row[mappedColumn]) {
          const value = row[mappedColumn].toString().trim()

          // Check minLength
          if (rules.minLength && value.length < rules.minLength)
            errors.push(`Row ${rowNumber}: ${fieldKey} must be at least ${rules.minLength} characters long`)

          // Check maxLength
          if (rules.maxLength && value.length > rules.maxLength)
            errors.push(`Row ${rowNumber}: ${fieldKey} must not exceed ${rules.maxLength} characters`)

          // Check pattern
          if (rules.pattern) {
            const regex = new RegExp(rules.pattern)
            if (!regex.test(value))
              errors.push(`Row ${rowNumber}: ${rules.message || `${fieldKey} format is invalid`}`)
          }

          // Check enum values
          if (rules.enum && !rules.enum.includes(value))
            errors.push(`Row ${rowNumber}: ${rules.message || `${fieldKey} must be one of: ${rules.enum.join(', ')}`}`)

          // Check numeric range
          if (rules.min !== undefined || rules.max !== undefined) {
            const numValue = Number(value)
            if (!Number.isNaN(numValue)) {
              if (rules.min !== undefined && numValue < rules.min)
                errors.push(`Row ${rowNumber}: ${fieldKey} must be at least ${rules.min}`)
              if (rules.max !== undefined && numValue > rules.max)
                errors.push(`Row ${rowNumber}: ${fieldKey} must not exceed ${rules.max}`)
            }
          }
        }
      })
    }

    // Check business rules
    if (validationRules.value.businessRules) {
      Object.values(validationRules.value.businessRules).forEach(rule => {
        if (rule.rule === 'at_least_one_required') {
          const hasAnyField = rule.fields.some(fieldKey => {
            const mappedColumn = fieldMapping.value[fieldKey]

            return mappedColumn && row[mappedColumn] && row[mappedColumn].toString().trim() !== ''
          })

          if (!hasAnyField)
            errors.push(`Row ${rowNumber}: ${rule.message}`)
        }
      })
    }
  })

  return { errors, warnings }
}

// Upload file, create CallList and import data to staging (async without blocking UI)
const uploadFileCreateCallListAndImport = async () => {
  if (!uploadedFile.value)
    throw new Error(t('calllist.errorMessages.noFileUploaded'))

  if (!callListDetails.value.listName)
    throw new Error(t('calllist.errorMessages.listNameRequired'))

  // Initialize progress tracking
  importProgress.value = {
    isProcessing: true,
    progress: 0,
    currentStep: t('calllist.uploadingFile'),
    error: null,
  }

  try {
    // Step 1: Upload file and create CallList
    importProgress.value.progress = 20
    importProgress.value.currentStep = t('calllist.uploadingFileAndCreating')

    const formData = new FormData()

    formData.append('file', uploadedFile.value)
    formData.append('importKey', callListDetails.value.importKey)
    formData.append('listName', callListDetails.value.listName)
    formData.append('listDetail', callListDetails.value.listDetail || '')
    formData.append('listComment', callListDetails.value.listComment || '')
    formData.append('startDate', callListDetails.value.startDate || '')
    formData.append('endDate', callListDetails.value.endDate || '')
    
    // ✅ เพิ่ม DNC Groups
    if (selectedDncGroups.value.length > 0) {
      formData.append('dncGroupIds', JSON.stringify(selectedDncGroups.value))
    }

    const uploadResponse = await $api('/call-lists/upload', {
      method: 'POST',
      body: formData,
    })

    // Store callListId for later use
    callListDetails.value.callListId = uploadResponse.id

    // Step 2: Import data to staging
    importProgress.value.progress = 50
    importProgress.value.currentStep = t('calllist.processingAndImporting')

    const importResponse = await $api(`/call-lists/import/${uploadResponse.id}`, {
      method: 'POST',
      body: {
        fieldMapping: fieldMapping.value,
      },
    })

    if (importResponse.success) {
      // Store summary for Step 2
      importSummary.value = importResponse.summary
      isImportCompleted.value = true

      // Complete progress
      importProgress.value.progress = 100
      importProgress.value.currentStep = t('calllist.importCompleted')
      importProgress.value.isProcessing = false

      showToast({
        type: 'success',
        message: t('calllist.importCompleted'),
      })
    }
    else {
      throw new Error(importResponse.message || t('calllist.errorMessages.importFailed'))
    }
  }
  catch (error) {
    // Handle error
    importProgress.value.error = (error as Error).message
    importProgress.value.isProcessing = false
    importProgress.value.currentStep = t('calllist.importFailed')
    showToast({
      type: 'error',
      message: (error as Error).message || t('calllist.error'),
    })
  }
}

// Reset function - clear data and start over
const resetCallList = async () => {
  loading.value = true

  try {
    // Call backend to delete LeadImportStaging data
    if (callListDetails.value.callListId) {
      await $api(`/call-lists/reset/${callListDetails.value.callListId}`, {
        method: 'DELETE',
      })
    }

    // Clear all data except basic call list info
    const originalListName = callListDetails.value.listName
    const originalListDetail = callListDetails.value.listDetail
    const originalListComment = callListDetails.value.listComment
    const originalStartDate = callListDetails.value.startDate
    const originalEndDate = callListDetails.value.endDate

    // Reset all state - clear file to force user to upload new file
    callListDetails.value = {
      importKey: '',
      callListId: null,
      listName: originalListName,
      listDetail: originalListDetail,
      listComment: originalListComment,
      fileName: '',
      fileType: '',
      fileSize: 0,
      totalRecords: 0,
      startDate: originalStartDate,
      endDate: originalEndDate,
    }

    // Clear file and all related data
    uploadedFile.value = null
    filePreview.value = []
    csvHeaders.value = []
    fieldMapping.value = {}
    importSummary.value = {
      totalRecords: 0,
      validRecords: 0,
      invalidRecords: 0,
      fileDuplicates: 0,
      calllistDuplicates: 0,
      dncRecords: 0,
      badListRecords: 0,
      importedRecords: 0,
    }
    isImportCompleted.value = false
    importProgress.value = {
      isProcessing: false,
      progress: 0,
      currentStep: '',
      error: null,
    }
    selectedCallListId.value = null
    availableCallLists.value = []

    // Go back to first step
    currentStep.value = 0

    // Regenerate import key
    callListDetails.value.importKey = generateImportKey()
  }
  catch (error) {
    showToast({
      type: 'error',
      message: (error as Error).message || t('calllist.error'),
    })
  }
  finally {
    loading.value = false
  }
}

// Final submission (if needed)
const submitCallList = async () => {
  // This can be used for final actions if needed

  // Redirect to calllist page
  await router.push('/calllist/list')
}

// Download records function
const downloadRecords = async (type: string) => {
  try {
    // Use $api to get the file as text
    const response = await $api(`/call-lists/download/${callListDetails.value.callListId}/${type}`, {
      method: 'GET',
    })

    if (response) {
      // Create download link
      const blob = new Blob([response], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')

      link.href = url
      link.download = `${type}_records_${new Date().toISOString().split('T')[0]}.csv`
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    }
    else {
      showToast({
        type: 'error',
        message: t('calllist.downloadFailed'),
      })
    }
  }
  catch (error) {
    showToast({
      type: 'error',
      message: `Download error: ${error.message}`,
    })
  }
}

// Submit handler
const onSubmit = async () => {
  loading.value = true

  try {
    // Validate import type selection
    if (importType.value === 'valid' && (!importSummary || importSummary.validRecords === 0))
      throw new Error('No valid records available for import')

    // Call backend to import data to Lead table
    await $api(`/call-lists/import-to-leads/${callListDetails.value.callListId}`, {
      method: 'POST',
      body: {
        importType: importType.value,
      },
    })

    showToast({
      type: 'success',
      message: t('calllist.callListCreatedSuccessfully'),
    })

    // Clear file state after successful import
    uploadedFile.value = null
    filePreview.value = []
    csvHeaders.value = []

    // Step 2: Final submission (redirect to calllist page)
    await submitCallList()
  }
  catch (error) {
    showToast({
      type: 'error',
      message: (error as Error).message || t('calllist.error'),
    })
  }
  finally {
    loading.value = false
  }
}

// Next step handler
const onNext = async () => {
  loading.value = true

  try {
    if (currentStep.value === 1) {
      // Step 1: Start upload process and move to next step immediately
      // Don't await - let it run in background
      uploadFileCreateCallListAndImport()

      // Move to next step immediately to show progress
      currentStep.value++
    }
    else {
      // Just move to next step
      currentStep.value++
    }
  }
  catch (error) {
    showToast({
      type: 'error',
      message: (error as Error).message || t('calllist.error'),
    })
  }
  finally {
    loading.value = false
  }
}

// Validation functions - only check field mapping requirements
const validateFieldMapping = () => {
  const errors: string[] = []

  // Check required fields - must be mapped to a column
  validationRules.value.requiredFields.forEach(fieldKey => {
    if (!fieldMapping.value[fieldKey]) {
      const field = availableFields.value.find(f => f.key === fieldKey)

      errors.push(`${field?.label || fieldKey} must be mapped to a column`)
    }
  })

  // Check business rules for field mapping
  if (validationRules.value.businessRules) {
    Object.values(validationRules.value.businessRules).forEach(rule => {
      if (rule.rule === 'at_least_one_required') {
        const hasAnyField = rule.fields.some(fieldKey => fieldMapping.value[fieldKey])
        if (!hasAnyField)
          errors.push(`${rule.fields.map(f => availableFields.value.find(af => af.key === f)?.label || f).join(' or ')} must be mapped to a column`)
      }
    })
  }

  return errors
}

// Field mapping validation errors
const fieldMappingErrors = computed(() => {
  return validateFieldMapping()
})

// Validation
const isStepValid = computed(() => {
  if (currentStep.value === 0)
    return !!uploadedFile.value && !!callListDetails.value.listName
  if (currentStep.value === 1) {
    // Only check required field mappings, not data validation
    const mappingErrors = validateFieldMapping()

    return mappingErrors.length === 0
  }
  if (currentStep.value === 2) {
    // Allow step if import is completed (not processing)
    return isImportCompleted.value && !importProgress.value.isProcessing
  }

  return false
})

// Step navigation status
const stepNavigationStatus = computed(() => {
  if (currentStep.value === 2) {
    if (importProgress.value.isProcessing) {
      return {
        canNavigate: false,
        message: t('calllist.importingData'),
        showProgress: true,
        showReset: false,
      }
    }

    if (isImportCompleted.value) {
      return {
        canNavigate: false,
        message: t('calllist.importCompleted'),
        showProgress: false,
        showReset: true,
      }
    }

    if (importProgress.value.error) {
      return {
        canNavigate: false,
        message: t('calllist.importFailed'),
        showProgress: false,
        showReset: true,
      }
    }
  }

  return {
    canNavigate: true,
    message: '',
    showProgress: false,
    showReset: false,
  }
})

// Check if step navigation should be disabled
const isStepNavigationDisabled = computed(() => {
  // Disable navigation when:
  // 1. Import is processing
  // 2. Import is completed (success)
  // 3. Import has error (failed)
  return importProgress.value.isProcessing || isImportCompleted.value || !!importProgress.value.error
})

// Get field icon based on type
const getFieldIcon = (type: string) => {
  if (type === 'text')
    return 'tabler-text'
  if (type === 'phone')
    return 'tabler-phone'
  if (type === 'number')
    return 'tabler-hash'
  if (type === 'email')
    return 'tabler-mail'
  if (type === 'date')
    return 'tabler-calendar'

  return 'tabler-field'
}

// Auto-generate import key when list name changes (but keep the 13-digit format)
watch(() => callListDetails.value.listName, newName => {
  if (newName && !callListDetails.value.importKey)
    callListDetails.value.importKey = generateImportKey()
})

// Watch for step changes
watch(currentStep, newStep => {
  if (newStep === 1) {
    // Load field mapping configuration when entering step 2
    fetchAvailableFields()

    // Load available call lists for field mapping reuse
    loadAvailableCallLists()
  }
})

// Lifecycle
onMounted(() => {
  fetchAvailableFields()
  loadAvailableDncGroups()
})
</script>

<template>
  <section>
    <!-- 👉 Page Header -->
    <div class="mb-6">
      <h1 class="text-h4 font-weight-bold mb-2">
        {{ t('calllist.createCallList') }}
      </h1>
      <p class="text-body-1 text-medium-emphasis">
        {{ t('calllist.wizardDescription') }}
      </p>
    </div>
    <VCard>
      <VCardText>
        <!-- 👉 Stepper -->
        <AppStepper
          v-model:current-step="currentStep"
          :items="numberedSteps"
          :is-active-step-valid="!isStepNavigationDisabled"
          class="stepper-icon-step-bg"
        />
      </VCardText>

      <VDivider />

      <VCardText>
        <!-- 👉 stepper content -->
        <VForm>
          <VWindow
            v-model="currentStep"
            class="disable-tab-transition"
          >
            <!-- Step 1: File Upload -->
            <VWindowItem>
              <VRow>
                <VCol cols="12">
                  <h6 class="text-h6 font-weight-medium">
                    {{ t('calllist.uploadFile') }}
                  </h6>
                  <p class="mb-0">
                    {{ t('calllist.wizardDescription') }}
                  </p>
                </VCol>

                <VCol cols="12">
                  <!-- File Upload Area -->
                  <VCard
                    :class="{ 'border-primary': isDragOver }"
                    variant="outlined"
                    @dragover="onDragOver"
                    @dragleave="onDragLeave"
                    @drop="onDrop"
                  >
                    <VCardText class="text-center py-8">
                      <VIcon
                        icon="tabler-cloud-upload"
                        size="48"
                        class="text-primary mb-4"
                      />
                      <h6 class="text-h6 mb-2">
                        {{ uploadedFile ? t('calllist.fileUploaded') : t('calllist.dragDropFile') }}
                      </h6>
                      <p class="text-body-2 mb-4">
                        {{ t('calllist.supportedFormats') }}
                      </p>

                      <VBtn
                        v-if="!uploadedFile"
                        color="primary"
                        @click="fileInput?.click()"
                      >
                        {{ t('calllist.selectFile') }}
                      </VBtn>

                      <input
                        ref="fileInput"
                        type="file"
                        accept=".csv,.txt,.xlsx,.xls"
                        style="display: none"
                        @change="onFileSelect"
                      >
                    </VCardText>
                  </VCard>

                  <!-- CallList Details -->
                  <VCol
                    cols="12"
                    md="6"
                  >
                    <AppTextField
                      v-model="callListDetails.listName"
                      :label="t('calllist.listName')"
                      :placeholder="t('calllist.enterCallListName')"
                      :rules="[requiredValidator]"
                      required
                    />
                  </VCol>

                  <VCol
                    cols="12"
                    md="6"
                  >
                    <AppTextField
                      v-model="callListDetails.listDetail"
                      :label="t('calllist.listDetail')"
                      :placeholder="t('calllist.enterCallListDescription')"
                    />
                  </VCol>

                  <VCol cols="12">
                    <AppTextField
                      v-model="callListDetails.listComment"
                      :label="t('calllist.listComment')"
                      :placeholder="t('calllist.enterAdditionalComments')"
                      type="textarea"
                      rows="3"
                    />
                  </VCol>

                  <!-- DNC Groups Selection -->
                  <VCol cols="12">
                    <AppSelect
                      v-model="selectedDncGroups"
                      :label="t('calllist.dncGroups')"
                      :placeholder="t('calllist.selectDncGroups')"
                      :items="availableDncGroups"
                      item-title="name"
                      item-value="id"
                      :loading="loadingDncGroups"
                      multiple
                      chips
                      clearable
                    >
                      <template #prepend-inner>
                        <VIcon icon="tabler-ban" />
                      </template>
                      <template #chip="{ props: chipProps, item }">
                        <VChip
                          v-bind="chipProps"
                          color="error"
                          size="small"
                        >
                          {{ item.raw.name }}
                        </VChip>
                      </template>
                      <template #item="{ props: itemProps, item }">
                        <VListItem v-bind="itemProps">
                          <template #prepend>
                            <VIcon icon="tabler-ban" color="error" />
                          </template>
                          <VListItemTitle>{{ item.raw.name }}</VListItemTitle>
                          <VListItemSubtitle v-if="item.raw.description">
                            {{ item.raw.description }}
                          </VListItemSubtitle>
                        </VListItem>
                      </template>
                    </AppSelect>
                    <div class="text-caption text-medium-emphasis mt-1">
                      <VIcon icon="tabler-info-circle" size="14" class="me-1" />
                      {{ t('calllist.dncGroupsHint') }}
                    </div>
                  </VCol>

                  <!-- File Info -->
                  <VCard
                    v-if="uploadedFile"
                    class="mt-4"
                    variant="outlined"
                    color="success"
                  >
                    <VCardText>
                      <div class="d-flex align-center">
                        <VIcon
                          icon="tabler-file-check"
                          class="text-success me-3"
                        />
                        <div>
                          <h6 class="text-h6">
                            {{ uploadedFile.name }}
                          </h6>
                          <div class="d-flex flex-wrap gap-4 mt-2">
                            <div class="text-body-2">
                              <strong>File Size:</strong> {{ (callListDetails.fileSize / 1024).toFixed(2) }} KB
                            </div>
                            <div class="text-body-2">
                              <strong>File Type:</strong> {{ callListDetails.fileType }}
                            </div>
                            <div class="text-body-2">
                              <strong>Import Key:</strong> {{ callListDetails.importKey }}
                            </div>
                            <div class="text-body-2">
                              <strong>Total Records:</strong> {{ callListDetails.totalRecords }}
                            </div>
                            <div class="text-body-2">
                              <strong>{{ detectedEncoding === 'excel' ? t('calllist.format') : t('calllist.encoding') }}:</strong> {{ detectedEncoding === 'excel' ? t('calllist.excel') : detectedEncoding }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </VCardText>
                  </VCard>

                  <!-- File Parsing Error Alert -->
                  <VAlert
                    v-if="encodingError"
                    type="warning"
                    variant="tonal"
                    class="mt-4"
                  >
                    <template #title>
                      File Parsing Issue
                    </template>
                    {{ encodingError }}
                  </VAlert>

                  <!-- File Preview -->
                  <VCard
                    v-if="filePreview.length > 0"
                    class="mt-4"
                    variant="outlined"
                  >
                    <VCardTitle>
                      {{ t('calllist.filePreview') }}
                    </VCardTitle>
                    <VCardText>
                      <VDataTable
                        :headers="csvHeaders.map(h => ({ title: h, key: h }))"
                        :items="filePreview"
                        density="compact"
                        hide-default-footer
                      />
                    </VCardText>
                  </VCard>
                </VCol>
              </VRow>
            </VWindowItem>

            <!-- Step 2: Field Mapping -->
            <VWindowItem>
              <VRow>
                <VCol cols="12">
                  <div class="d-flex align-center justify-space-between mb-4">
                    <div>
                      <h6 class="text-h6 font-weight-medium">
                        {{ t('calllist.mapFields') }}
                      </h6>
                      <p class="mb-0">
                        {{ t('calllist.mapFieldsDescription') }}
                      </p>
                    </div>
                    <div class="d-flex align-center gap-3">
                      <VSelect
                        v-model="selectedCallListId"
                        :items="availableCallLists"
                        item-title="listName"
                        item-value="id"
                        :label="t('calllist.selectCallListForMapping')"
                        :placeholder="t('calllist.selectCallListForMapping')"
                        :loading="loadingCallLists"
                        clearable
                        density="compact"
                        style="min-width: 200px;"
                        class="me-2"
                      >
                        <template #item="{ props, item }">
                          <VListItem v-bind="props">
                            <VListItemTitle>{{ item.raw.listName }}</VListItemTitle>
                            <VListItemSubtitle>
                              {{ formatDate24h(item.raw.createdAt) }}
                            </VListItemSubtitle>
                          </VListItem>
                        </template>
                      </VSelect>
                      <VBtn
                        color="primary"
                        variant="outlined"
                        prepend-icon="tabler-wand"
                        @click="autoMapFields"
                      >
                        {{ t('calllist.autoMapping') }}
                      </VBtn>
                    </div>
                  </div>
                </VCol>

                <!-- Validation Errors -->
                <VCol
                  v-if="fieldMappingErrors.length > 0"
                  cols="12"
                >
                  <VAlert
                    type="warning"
                    variant="tonal"
                    class="mb-4"
                  >
                    <template #title>
                      {{ t('calllist.validationErrors') }}
                    </template>
                    <ul class="mb-0">
                      <li
                        v-for="error in fieldMappingErrors"
                        :key="error"
                      >
                        {{ error }}
                      </li>
                    </ul>
                  </VAlert>
                </VCol>

                <VCol
                  v-for="field in availableFields"
                  :key="field.key"
                  cols="12"
                  md="6"
                >
                  <VSelect
                    v-model="fieldMapping[field.key]"
                    :label="field.label"
                    :placeholder="t('calllist.selectColumn')"
                    :items="csvHeaders"
                    :required="field.required"
                    clearable
                  >
                    <template #prepend>
                      <VIcon
                        :icon="getFieldIcon(field.type)"
                        size="20"
                      />
                    </template>
                    <template #append>
                      <VChip
                        :color="field.source === 'lead' ? 'primary' : 'secondary'"
                        size="x-small"
                        variant="tonal"
                      >
                        {{ field.source === 'lead' ? t('calllist.lead') : t('calllist.custom') }}
                      </VChip>
                    </template>
                  </VSelect>
                </VCol>
              </VRow>
            </VWindowItem>

            <!-- Step 3: CallList Details -->
            <VWindowItem>
              <VRow>
                <VCol cols="12">
                  <h6 class="text-h6 font-weight-medium">
                    {{ t('calllist.callListDetails') }}
                  </h6>
                  <p class="mb-0">
                    {{ t('calllist.enterCallListInformation') }}
                  </p>
                </VCol>

                <!-- Progress Bar for Import Process -->
                <VCol
                  v-if="importProgress.isProcessing"
                  cols="12"
                >
                  <VCard
                    variant="outlined"
                    color="primary"
                  >
                    <VCardText>
                      <div class="d-flex align-center mb-3">
                        <VIcon
                          icon="tabler-upload"
                          class="me-2"
                        />
                        <span class="text-h6">{{ t('calllist.importingData') }}</span>
                      </div>

                      <VProgressLinear
                        :model-value="importProgress.progress"
                        color="primary"
                        height="8"
                        rounded
                        class="mb-3"
                      />

                      <div class="d-flex justify-space-between align-center">
                        <span class="text-body-2">{{ importProgress.currentStep }}</span>
                        <span class="text-body-2 font-weight-medium">{{ importProgress.progress }}%</span>
                      </div>
                    </VCardText>
                  </VCard>
                </VCol>

                <!-- Status/Error Display -->
                <VCol
                  v-if="importProgress.error || stepNavigationStatus.message"
                  cols="12"
                >
                  <VAlert
                    :type="importProgress.error ? 'error' : 'info'"
                    variant="tonal"
                    class="mb-4"
                  >
                    <template #title>
                      {{ importProgress.error ? t('calllist.importFailed') : '' }}
                    </template>
                    {{ importProgress.error || stepNavigationStatus.message }}
                  </VAlert>
                </VCol>

                <!-- File Information Summary -->
                <VCol cols="12">
                  <VCard
                    variant="outlined"
                    color="primary"
                  >
                    <VCardTitle class="text-h6">
                      <VIcon
                        icon="tabler-file-info"
                        class="me-2"
                      />
                      File Information
                    </VCardTitle>
                    <VCardText>
                      <VRow>
                        <VCol
                          cols="12"
                          md="6"
                        >
                          <div class="text-body-2 mb-2">
                            <strong>File Name:</strong> {{ callListDetails.fileName }}
                          </div>
                          <div class="text-body-2 mb-2">
                            <strong>File Type:</strong> {{ callListDetails.fileType }}
                          </div>
                          <div class="text-body-2 mb-2">
                            <strong>File Size:</strong> {{ (callListDetails.fileSize / 1024).toFixed(2) }} KB
                          </div>
                        </VCol>
                        <VCol
                          cols="12"
                          md="6"
                        >
                          <div class="text-body-2 mb-2">
                            <strong>Import Key:</strong> {{ callListDetails.importKey }}
                          </div>
                          <div class="text-body-2 mb-2">
                            <strong>Total Records:</strong> {{ callListDetails.totalRecords }}
                          </div>
                          <div class="text-body-2 mb-2">
                            <strong>Status:</strong>
                            <VChip
                              color="success"
                              size="small"
                            >
                              {{ t('calllist.readyForImport') }}
                            </VChip>
                          </div>
                        </VCol>
                      </VRow>
                    </VCardText>
                  </VCard>
                </VCol>

                <!-- Import Summary -->
                <VCol
                  v-if="isImportCompleted"
                  cols="12"
                >
                  <VCard
                    variant="outlined"
                    color="success"
                  >
                    <VCardTitle class="text-h6">
                      <VIcon
                        icon="tabler-chart-bar"
                        class="me-2"
                      />
                      Import Summary
                    </VCardTitle>
                    <VCardText>
                      <VRow>
                        <VCol
                          cols="12"
                          md="3"
                        >
                          <div class="text-center">
                            <div class="d-flex align-center justify-center gap-2 mb-1">
                              <div class="text-h4 text-primary">
                                {{ importSummary.totalRecords }}
                              </div>
                              <VBtn
                                v-if="importSummary.totalRecords > 0"
                                icon
                                size="small"
                                variant="text"
                                color="primary"
                                @click="downloadRecords('total')"
                              >
                                <VIcon icon="tabler-download" />
                              </VBtn>
                            </div>
                            <div class="text-body-2">
                              {{ t('calllist.totalRecords') }}
                            </div>
                          </div>
                        </VCol>
                        <VCol
                          cols="12"
                          md="3"
                        >
                          <div class="text-center">
                            <div class="d-flex align-center justify-center gap-2 mb-1">
                              <div class="text-h4 text-success">
                                {{ importSummary.validRecords }}
                              </div>
                              <VBtn
                                v-if="importSummary.validRecords > 0"
                                icon
                                size="small"
                                variant="text"
                                color="success"
                                @click="downloadRecords('valid')"
                              >
                                <VIcon icon="tabler-download" />
                              </VBtn>
                            </div>
                            <div class="text-body-2">
                              {{ t('calllist.validRecords') }}
                            </div>
                          </div>
                        </VCol>
                        <VCol
                          cols="12"
                          md="3"
                        >
                          <div class="text-center">
                            <div class="d-flex align-center justify-center gap-2 mb-1">
                              <div class="text-h4 text-warning">
                                {{ importSummary.fileDuplicates }}
                              </div>
                              <VBtn
                                v-if="importSummary.fileDuplicates > 0"
                                icon
                                size="small"
                                variant="text"
                                color="warning"
                                @click="downloadRecords('fileDuplicates')"
                              >
                                <VIcon icon="tabler-download" />
                              </VBtn>
                            </div>
                            <div class="text-body-2">
                              {{ t('calllist.fileDuplicates') }}
                            </div>
                          </div>
                        </VCol>
                        <VCol
                          cols="12"
                          md="3"
                        >
                          <div class="text-center">
                            <div class="d-flex align-center justify-center gap-2 mb-1">
                              <div class="text-h4 text-error">
                                {{ importSummary.dncRecords }}
                              </div>
                              <VBtn
                                v-if="importSummary.dncRecords > 0"
                                icon
                                size="small"
                                variant="text"
                                color="error"
                                @click="downloadRecords('dnc')"
                              >
                                <VIcon icon="tabler-download" />
                              </VBtn>
                            </div>
                            <div class="text-body-2">
                              {{ t('calllist.dncRecords') }}
                            </div>
                          </div>
                        </VCol>
                        <VCol
                          cols="12"
                          md="3"
                        >
                          <div class="text-center">
                            <div class="d-flex align-center justify-center gap-2 mb-1">
                              <div class="text-h4 text-info">
                                {{ importSummary.calllistDuplicates }}
                              </div>
                              <VBtn
                                v-if="importSummary.calllistDuplicates > 0"
                                icon
                                size="small"
                                variant="text"
                                color="info"
                                @click="downloadRecords('calllistDuplicates')"
                              >
                                <VIcon icon="tabler-download" />
                              </VBtn>
                            </div>
                            <div class="text-body-2">
                              {{ t('calllist.systemDuplicates') }}
                            </div>
                          </div>
                        </VCol>
                      </VRow>
                      <VDivider class="my-4" />
                      <VRow>
                        <VCol
                          cols="12"
                          md="4"
                        >
                          <div class="text-center">
                            <div class="d-flex align-center justify-center gap-2 mb-1">
                              <div class="text-h4 text-error">
                                {{ importSummary.invalidRecords }}
                              </div>
                              <VBtn
                                v-if="importSummary.invalidRecords > 0"
                                icon
                                size="small"
                                variant="text"
                                color="error"
                                @click="downloadRecords('invalid')"
                              >
                                <VIcon icon="tabler-download" />
                              </VBtn>
                            </div>
                            <div class="text-body-2">
                              {{ t('calllist.invalidRecords') }}
                            </div>
                          </div>
                        </VCol>
                        <VCol
                          cols="12"
                          md="4"
                        >
                          <div class="text-center">
                            <div class="text-h4 text-primary mb-1">
                              {{ importSummary.importedRecords }}
                            </div>
                            <div class="text-body-2">
                              {{ t('calllist.readyToImport') }}
                            </div>
                          </div>
                        </VCol>
                        <VCol
                          cols="12"
                          md="4"
                        >
                          <div class="text-center">
                            <VChip
                              :color="importSummary.importedRecords > 0 ? 'success' : 'error'"
                              size="large"
                              class="mb-1"
                            >
                              {{ importSummary.importedRecords > 0 ? t('calllist.ready') : t('calllist.noValidData') }}
                            </VChip>
                            <div class="text-body-2">
                              {{ t('calllist.status') }}
                            </div>
                          </div>
                        </VCol>
                      </VRow>
                    </VCardText>
                  </VCard>
                </VCol>

                <VCol
                  cols="12"
                  md="6"
                >
                  <AppTextField
                    :value="callListDetails.importKey"
                    :label="t('calllist.importKey')"
                    :placeholder="t('calllist.autoGeneratedKey')"
                    required
                    readonly
                    :hint="t('calllist.keyAutomaticallyGenerated')"
                  />
                </VCol>

                <VCol
                  cols="12"
                  md="6"
                >
                  <AppTextField
                    v-model="callListDetails.listName"
                    :label="t('calllist.listName')"
                    :placeholder="t('calllist.placeholderCallListName')"
                    :rules="[requiredValidator]"
                    required
                  />
                </VCol>

                <VCol cols="12">
                  <VTextarea
                    v-model="callListDetails.listDetail"
                    :label="t('calllist.listDetail')"
                    :placeholder="t('calllist.enterCallListDescription')"
                    rows="3"
                  />
                </VCol>

                <VCol cols="12">
                  <VTextarea
                    v-model="callListDetails.listComment"
                    :label="t('calllist.listComment')"
                    :placeholder="t('calllist.enterAdditionalComments')"
                    rows="3"
                  />
                </VCol>

                <VCol
                  cols="12"
                  md="6"
                >
                  <AppDateTimePicker
                    v-model="callListDetails.startDate"
                    :label="t('calllist.startDate')"
                    :placeholder="t('calllist.selectStartDate')"
                    type="date"
                  />
                </VCol>

                <VCol
                  cols="12"
                  md="6"
                >
                  <AppDateTimePicker
                    v-model="callListDetails.endDate"
                    :label="t('calllist.endDate')"
                    :placeholder="t('calllist.selectEndDate')"
                    type="date"
                  />
                </VCol>

                <VCol cols="12">
                  <VLabel class="text-body-1 font-weight-medium mb-2">
                    {{ t('calllist.importType') }}
                  </VLabel>
                  <VRadioGroup
                    v-model="importType"
                    inline
                  >
                    <VRadio
                      value="valid"
                      :label="t('calllist.importValidOnly')"
                      :disabled="!importSummary || importSummary.validRecords === 0"
                    />
                    <VRadio
                      value="all"
                      :label="t('calllist.importAllRecords')"
                    />
                  </VRadioGroup>
                  <div class="text-caption text-medium-emphasis mt-1">
                    {{ t('calllist.importTypeDescription') }}
                  </div>
                </VCol>
              </VRow>
            </VWindowItem>
          </VWindow>

          <div class="d-flex flex-wrap gap-4 justify-sm-space-between justify-center mt-8">
            <VBtn
              color="secondary"
              variant="tonal"
              :disabled="currentStep === 0 || (currentStep === 2 && (isImportCompleted || !!importProgress.error))"
              @click="currentStep--"
            >
              <VIcon
                icon="tabler-arrow-left"
                start
                class="flip-in-rtl"
              />
              {{ t('common.previous') }}
            </VBtn>

            <!-- Reset Button (only show when import failed) -->
            <VBtn
              v-if="stepNavigationStatus.showReset"
              color="warning"
              variant="tonal"
              :loading="loading"
              @click="resetCallList"
            >
              <VIcon
                icon="tabler-refresh"
                start
                class="flip-in-rtl"
              />
              {{ t('calllist.reset') }}
            </VBtn>

            <VBtn
              v-if="numberedSteps.length - 1 === currentStep"
              color="success"
              :loading="loading"
              :disabled="!isStepValid || !callListDetails.listName || !importType"
              @click="onSubmit"
            >
              {{ t('common.submit') }}
            </VBtn>

            <VBtn
              v-else
              :disabled="!isStepValid"
              :loading="loading"
              @click="onNext"
            >
              {{ t('common.next') }}

              <VIcon
                icon="tabler-arrow-right"
                end
                class="flip-in-rtl"
              />
            </VBtn>
          </div>
        </VForm>
      </VCardText>
    </VCard>
  </section>
</template>
